// One-shot recovery for a tab whose build a deploy replaced: the chunk it asks
// for is gone and only a full page load fixes it. See CLAUDE.md (CPRD-5916).

const RELOAD_FLAG = 'auxo:stale-build-reload';

// The reload target — vite:preloadError fires first and carries no route.
let pendingNavigation = '';

// Whether this page instance is already navigating away.
let reloadStarted = false;

export function setPendingNavigation(fullPath) {
  pendingNavigation = fullPath || '';
}

export function getPendingNavigation() {
  return pendingNavigation;
}

// sessionStorage throws outright in Safari private mode, and a guard that
// throws would take down the error handler it exists to protect.
function readFlag() {
  try {
    return sessionStorage.getItem(RELOAD_FLAG);
  } catch {
    return null;
  }
}

function writeFlag(value) {
  try {
    sessionStorage.setItem(RELOAD_FLAG, value);
  } catch {
    // ignore — see readFlag
  }
}

export function hasAttemptedStaleBuildReload() {
  return readFlag() !== null;
}

export function markStaleBuildReloadAttempted() {
  writeFlag(String(Date.now()));
}

export function clearStaleBuildReload() {
  reloadStarted = false;
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore — see readFlag
  }
}

// Wording differs by browser, so match shapes rather than one string. Anything
// unrecognised is left alone: a reload cannot fix a real application error.
const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /expected a javascript(-or-wasm)? module script/i,
  /unable to preload css/i,
  /'text\/html' is not a valid javascript mime type/i,
];

export function isChunkLoadError(error) {
  if (!error) return false;

  const message = String(error.message ?? error);
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Re-anchors a router path on BASE_URL, which createWebHistory strips from
 * fullPath — the same trap as the callback-token scrub in router/index.js.
 */
export function resolveReloadUrl(
  targetPath,
  base = import.meta.env.BASE_URL || '/'
) {
  if (!targetPath) return '';

  // Every leading slash: `//evil.com` would send location.assign() off-site.
  const path = String(targetPath).replace(/^\/+/, '');
  return `${base.replace(/\/+$/, '')}/${path}`;
}

/**
 * Handles `vite:preloadError`; the event carries no route, so the target is
 * whatever navigation is in flight. Never prevents the default — see CLAUDE.md
 * ("Surviving a deploy with the tab open").
 */
export function handlePreloadError() {
  reloadForStaleBuild();
}

/**
 * Hard-loads the app once per tab session to pick up the current build, landing
 * on `targetPath` or on the navigation in flight.
 *
 * @returns {boolean} whether a reload was actually started.
 */
export function reloadForStaleBuild(targetPath = '') {
  // One failure is reported twice — preloadError, then the router — and the
  // second arrives while this page is already on its way out.
  if (reloadStarted) return false;

  // Only a failure the user is waiting on earns a reload: imports that fire
  // outside navigation would keep failing after it, and loop. See CLAUDE.md.
  const url = resolveReloadUrl(targetPath || getPendingNavigation());
  if (!url) return false;

  if (hasAttemptedStaleBuildReload()) {
    console.warn(
      'Chunk load failed again after reloading for a stale build — ' +
        'not reloading a second time.'
    );
    return false;
  }

  reloadStarted = true;
  markStaleBuildReloadAttempted();

  window.location.assign(url);
  return true;
}
